import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/products", label: "Products" },
    { to: "/industries", label: "Industries" },
    { to: "/quality-logistics", label: "Quality & Logistics" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-coal-900/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
                {/* Logo */}
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center group"
                >
                    <div className="font-geometric leading-none">
                        <div className="text-white font-extrabold text-xl sm:text-2xl tracking-[0.16em]">
                            CARBEX
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="block h-px w-3 bg-ember" />
                            <span className="text-[9px] sm:text-[10px] text-ember font-bold tracking-[0.32em] uppercase">
                                International
                            </span>
                            <span className="block h-px w-3 bg-ember" />
                        </div>
                        <div className="text-[8px] sm:text-[9px] text-zinc-500 font-semibold tracking-[0.32em] uppercase mt-1 text-center">
                            Private Limited
                        </div>
                    </div>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
                            className={({ isActive }) =>
                                `px-4 py-2 text-sm uppercase tracking-wider font-medium transition-colors ${
                                    isActive
                                        ? "text-white"
                                        : "text-zinc-400 hover:text-white"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                {/* CTA */}
                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href="tel:9872227812"
                        data-testid="nav-phone"
                        className="text-zinc-400 hover:text-white flex items-center gap-2 text-sm"
                    >
                        <Phone className="w-4 h-4" />
                        <span className="font-mono">+91 98722 27812</span>
                    </a>
                    <Link
                        to="/contact"
                        data-testid="nav-enquiry-btn"
                        className="bg-ember hover:bg-ember-hover text-white px-5 py-3 text-xs font-bold tracking-[0.18em] uppercase transition-colors"
                    >
                        Enquire Now
                    </Link>
                </div>

                {/* Mobile menu trigger */}
                <button
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((o) => !o)}
                    className="lg:hidden text-white p-2"
                >
                    {open ? <X /> : <Menu />}
                </button>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    className="lg:hidden bg-coal-900 border-t border-white/10"
                    data-testid="nav-mobile-menu"
                >
                    <div className="px-6 py-6 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `py-3 text-base uppercase tracking-wider font-medium border-b border-white/5 ${
                                        isActive
                                            ? "text-ember"
                                            : "text-zinc-300 hover:text-white"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            className="mt-4 bg-ember hover:bg-ember-hover text-white py-4 text-center text-xs font-bold tracking-[0.18em] uppercase"
                        >
                            Enquire Now
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
